import React, { useEffect, useRef, useState } from "react";
import styles from "./CreateReportModal.module.css";
import { reactIcons } from "../../../icons/reactIcons";
import { homeApis } from "../../../apis/homeApis";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux-store/confirmSnackbarSlice";
import { reverseGeocode } from "../../../utils/reverseGeocode";

interface CreateReportModalProps {
  open: boolean;
  onClose: () => void;
  coords?: { lat: number; lng: number } | null;
  refreshReports?: () => void;
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  open,
  onClose,
  coords = null,
  refreshReports,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showPickerPopup, setShowPickerPopup] = useState(false);

  const [showDesktopCamera, setShowDesktopCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (!coords) return;

    (async () => {
      const addr = await reverseGeocode(coords.lat, coords.lng);
      setAddress(addr);
    })();
  }, [coords]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("create-modal-open");
    } else {
      document.body.classList.remove("create-modal-open");
    }

    return () => document.body.classList.remove("create-modal-open");
  }, [open]);

  if (!open) return null;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      const imageUrls = files.map((file) => URL.createObjectURL(file));
      setSelectedImages((prev) => [...prev, ...imageUrls]);
    }

    e.target.value = "";

    setShowPickerPopup(false);
  };

  const handleRemoveImage = (url: string) => {
    setSelectedImages((prev) => prev.filter((img) => img !== url));
  };

  const handleUploadClick = () => {
    setShowPickerPopup(true);
  };

  const openDesktopCamera = async () => {
    try {
      setShowDesktopCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      alert("Camera access blocked or unavailable.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx?.drawImage(video, 0, 0);

    const imgURL = canvas.toDataURL("image/png");

    setSelectedImages((prev) => [...prev, imgURL]);
    stopDesktopCamera();
    setShowDesktopCamera(false);

    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const stopDesktopCamera = () => {
    const stream = videoRef.current?.srcObject as MediaStream;
    stream?.getTracks().forEach((t) => t.stop());
  };

  const urlToFile = async (url: string, filename = "report-image.png") => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || "image/png" });
  };

  const submitReport = async () => {
    try {
      if (!coords) {
        dispatch(
          showSnackbar({
            type: "error",
            message: "Please confirm location first.",
            autoCloseDuration: 3000,
          })
        );
        return;
      }

      setSubmitting(true);

      const fileIds: string[] = [];

      for (const imgUrl of selectedImages) {
        const file = await urlToFile(imgUrl);
        const res: any = await homeApis.uploadFile(file, "report");

        if (!res?.data) {
          throw new Error("File upload failed.");
        }

        fileIds.push(res.data);
      }

      const payload = {
        notes: noteRef.current?.value || "",
        geoTag: {
          lat: coords.lat.toString(),
          lon: coords.lng.toString(),
          address: address,
        },
        fileIds,
      };

      await homeApis.createReport(payload);

      dispatch(
        showSnackbar({
          type: "success",
          message: "Report submitted successfully!",
          autoCloseDuration: 3000,
        })
      );

      onClose();
      refreshReports?.();

      setSelectedImages([]);
      if (noteRef.current) noteRef.current.value = "";
    } catch (err) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "Failed to submit report. Please try again.",
          autoCloseDuration: 3000,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Create New Report</h3>
          <reactIcons.RxCrossIcon
            style={{ fontSize: "20px", fontWeight: "700", cursor: "pointer" }}
            onClick={() => {
              onClose();
              setSelectedImages([]);
            }}
          />
        </div>

        <div className={styles.modalBody}>
          <div className={styles.section}>
            <label className={styles.label}>
              Images <span className={styles.required}>*</span>
            </label>

            <div className={styles.uploadBox} onClick={handleUploadClick}>
              <reactIcons.IoCameraIcon fontSize={28} />
              <p>Upload Image</p>
              <span>Tap to take or select an Image</span>
            </div>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={cameraInputRef}
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />
            <input
              type="file"
              accept="image/*"
              multiple
              ref={galleryInputRef}
              onChange={handleImageUpload}
              style={{ display: "none" }}
            />

            {selectedImages.length > 0 && (
              <div className={styles.previewGrid}>
                {selectedImages.map((img, i) => (
                  <div key={i} className={styles.imagePreview}>
                    <img
                      src={img}
                      alt={`Preview ${i}`}
                      className={styles.previewImg}
                    />
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => handleRemoveImage(img)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.section}>
            <label className={styles.label}>Note</label>
            <textarea
              ref={noteRef}
              className={styles.textarea}
              placeholder="Residential renovation without permit notice."
            ></textarea>
          </div>

          <div className={styles.section}>
            <label className={styles.label}>Location Information</label>
            <div className={styles.locationBox}>
              <reactIcons.IoLocationOutlineIcon fontSize={24} color="#0155AE" />
              <div>
                <p className={styles.address}>
                  {coords
                    ? address || "Fetching address..."
                    : "Error Fetching Address"}
                </p>

                <span className={styles.coords}>
                  {coords
                    ? `${coords.lat.toFixed(4)}° N, ${coords.lng.toFixed(4)}° W`
                    : "40.7484° N, 73.9857° W"}
                </span>
              </div>
            </div>
            <p className={styles.locationNote}>
              📍 Location is automatically captured and cannot be edited
            </p>
          </div>

          <button
            className={styles.submitBtn}
            onClick={submitReport}
            disabled={submitting || selectedImages.length === 0}
            type="button"
          >
            {submitting ? "Submitting..." : "Submit Report"}
          </button>
        </div>
      </div>

      {showPickerPopup && (
        <div
          className={styles.popupOverlay}
          onClick={() => setShowPickerPopup(false)}
        >
          <div className={styles.popupBox} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.popupBtn}
              onClick={() => {
                if (isMobile) {
                  cameraInputRef.current?.click();
                } else {
                  openDesktopCamera();
                }
              }}
            >
              <reactIcons.IoCameraIcon fontSize={20} />
              Take a Photo
            </button>

            <button
              className={styles.popupBtn}
              onClick={() => galleryInputRef.current?.click()}
            >
              <reactIcons.IoImagesOutlineIcon fontSize={20} />
              Select from Gallery
            </button>

            <button
              className={styles.popupCancel}
              onClick={() => setShowPickerPopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showDesktopCamera && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            flexDirection: "column",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            style={{
              width: "80%",
              maxWidth: "500px",
              background: "#000",
              borderRadius: "10px",
            }}
          ></video>

          <canvas ref={canvasRef} style={{ display: "none" }} />

          <button
            onClick={capturePhoto}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              background: "#007bff",
              color: "#fff",
              border: "none",
            }}
          >
            Capture Photo
          </button>

          <button
            onClick={() => {
              stopDesktopCamera();
              setShowDesktopCamera(false);
            }}
            style={{
              marginTop: "10px",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              background: "#ccc",
              color: "#000",
              border: "none",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateReportModal;
